import { useContext, useEffect, useState } from 'react';

import { ProvidersByPartner } from 'src/modules/designStudio/helpers/productSelection/contractorFilteringPreferences';
import { Mode } from 'src/modules/designStudio/providers';
import { context } from 'src/modules/designStudio/providers/ModeProvider';
import { useProductsRouter } from 'src/modules/designStudio/providers/ProductCatalogProvider/useProductsRouter';
import { client as apolloClient } from 'src/shared/graphql';
import { queries } from 'src/shared/graphql/requests';
import { useJobId } from 'src/shared/hooks/useJobId';

/**
 * @description This query fetches the manufacturer preferences of the org that the user belongs to.
 */
const fetchUserOrgManufacturerPreferences = async (
  isPrintDebugOn: boolean,
): Promise<number[]> => {
  const response = await queries.orgManufacturers(apolloClient, {});
  if (isPrintDebugOn) {
    // eslint-disable-next-line no-console
    console.info(`User query: ${JSON.stringify(response, null, 2)}`);
  }
  return response?.data.profile.user.orgs[0]?.manufacturerIds ?? [];
};

/**
 * @description This query fetches the manufacturer preferences of the org that sent the connect request.
 * If the job was not sent as a connect request this will be undefined.
 */
const fetchConnectManufacturerPreferences = async (
  jobID: number,
  isPrintDebugOn: boolean,
): Promise<undefined | number[]> => {
  const response = await queries.jobManufacturerPreferences(apolloClient, {
    id: jobID,
  });

  const data = response?.data;
  if (!data) {
    return undefined;
  }

  if (isPrintDebugOn) {
    // eslint-disable-next-line no-console
    console.info(`Job query: ${JSON.stringify(response, null, 2)}`);
  }

  return (
    data.jobs.results[0]?.captureRequest?.displayRequestingOrg
      ?.manufacturerIds ?? undefined
  );
};

/**
 * Orgs at Hover only support selling some of the manufacturers we carry in our product catalog.
 * Orgs don't want to show customers products they don't sell. This useEffect filters out
 * manufacturer's that an org doesn't sell.
 *
 * We fetch the data of what manufacturers an org supports through the profile query, and then
 * prune our real data store accordingly.
 *
 * When we are in guest mode, we do not have access to the org present, and we instead rely on the
 * partner variable to keep track of what mode we are in. In Guest mode we use the partner to filter
 * out products not supported by that partner, this list of manufacturers that a partner supports can be found
 * in: src/modules/designStudio/helpers/productSelection/providersByPartner.ts
 */
export const useFilteredProductsRouter = ({
  isPrintDebugOn,
}: {
  isPrintDebugOn: boolean;
}) => {
  const { productsRouter } = useProductsRouter({ isPrintDebugOn });

  const { mode, partner } = useContext(context);
  const jobId = useJobId();

  const [
    hasManufacturerFilteringBeenDone,
    setHasManufacturerFilteringBeenDone,
  ] = useState(false);

  /**
   * The GraphQL query to fetch manufacturer ids requires the user to be authenticated. If they are not
   * design studio will redirect to the login screen. We only want to make this query in default mode
   * because we know that the user will be logged in.
   */
  const isSafeToFetchManufacturerPreferences = mode === Mode.DEFAULT;

  const [orgProviderAllowList, setOrgProviderAllowList] = useState<
    number[] | undefined
  >(undefined);

  /**
   * This useEffect handles fetching orgProviderAllowList, which will then be used to filter
   * products in productsRouter.
   */
  useEffect(() => {
    if (
      isSafeToFetchManufacturerPreferences &&
      jobId &&
      orgProviderAllowList === undefined
    ) {
      const fetchManufacturerPreferences = async () => {
        const [
          userOrgManufacturerPreferences,
          connectOrgManufacturerPreferences,
        ] = await Promise.all([
          fetchUserOrgManufacturerPreferences(isPrintDebugOn),
          fetchConnectManufacturerPreferences(jobId, isPrintDebugOn),
        ]);

        /**
         * If the job is a connect job then we want to use the connect orgs preferences, otherwise
         * use the user orgs preferences.
         */
        const manufacturerPreferences =
          connectOrgManufacturerPreferences ?? userOrgManufacturerPreferences;

        setOrgProviderAllowList(manufacturerPreferences);
      };
      void fetchManufacturerPreferences();
    }
  }, [
    isSafeToFetchManufacturerPreferences,
    isPrintDebugOn,
    jobId,
    orgProviderAllowList,
  ]);

  /**
   * This useEffect handles filtering products in productsRouter.
   * This can happen in two ways.
   *
   * 1. The user is logged, and we filter using the org's provider allow list.
   * 2. The user is anonymous, and we filter based on partner.
   */
  useEffect(() => {
    if (!hasManufacturerFilteringBeenDone && productsRouter) {
      if (mode === Mode.DEFAULT && orgProviderAllowList !== undefined) {
        if (isPrintDebugOn) {
          // eslint-disable-next-line no-console
          console.info(
            `In useFilteredProductsRouter: filtering manufacturers by org settings.`,
          );
        }
        productsRouter.removeProvidersNotInListFromRealStore(
          orgProviderAllowList.map(id => `${id}`),
        );
        setHasManufacturerFilteringBeenDone(true);
      } else if (mode !== Mode.DEFAULT && ProvidersByPartner[partner]) {
        if (isPrintDebugOn) {
          // eslint-disable-next-line no-console
          console.info(
            `In useFilteredProductsRouter: filtering manufacturers by partner = ${partner}`,
          );
        }
        productsRouter.removeProvidersNotInListFromRealStore(
          ProvidersByPartner[partner] as string[],
        );
        setHasManufacturerFilteringBeenDone(true);
      }
    }
  }, [
    orgProviderAllowList,
    productsRouter,
    partner,
    hasManufacturerFilteringBeenDone,
    mode,
    isPrintDebugOn,
  ]);

  return { productsRouter };
};
